import {
    useCallback,
    useState,
    useMemo,
} from 'react'

import Button from 'app/shared-components/Button'
import ButtonContainer from 'app/shared-components/ButtonContainer'
import {
    RequiredContainersCustomer,
} from 'app/types/common.types'
import {
    SelectedContainer,
    RequiredContainersInternal,
} from 'app/Apps/OrderManagement/Orders/orders.types'

import {
    BookedContainer,
    RejectedContainer,
} from './bookedContainers.types'
import getSelectedContainersToReject from './utils/getSelectedContainersToReject'
import getSelectedContainersToRebook from './utils/getSelectedContainersToRebook'
import ValidationBookedContainers from './ValidationBookedContainers'
import useRejectContainers from './hooks/useRejectContainers'
import useRebookContainers from './hooks/useRebookContainers'
import BookedContainersTable from './BookedContainersTable'

type Props = {
    value: SelectedContainer[],
    disabled: boolean,
    onSuccess: () => void,
    orderNumber: string,
    orderVersion: number,
    isInternalType: boolean,
    requiredContainers: RequiredContainersCustomer[] | RequiredContainersInternal[],
}

const BookedContainers = ({
    value,
    disabled,
    onSuccess,
    orderNumber,
    orderVersion,
    isInternalType,
    requiredContainers = [],
}: Props) => {
    const [
        data,
        setData,
    ] = useState<BookedContainer[]>(value)

    const selectedContainersToReject = useMemo((): RejectedContainer[] => {
        return getSelectedContainersToReject(data)
    }, [data])

    const selectedContainersToRebook = useMemo((): { id: number }[] => {
        return getSelectedContainersToRebook(data)
    }, [data])

    const {
        mutate: mutateRejectContainers,
        isPending: isLoadingRejectContainers,
    } = useRejectContainers(onSuccess)
    const {
        mutate: mutateRebookContainers,
        isPending: isLoadingRebookContainers,
    } = useRebookContainers(onSuccess)

    const onChange = useCallback((newValue: BookedContainer): void => {
        const updatedValue = data.map((item) => {
            if (item.id === newValue.id) {
                return newValue
            }

            return item
        })

        setData(updatedValue)
    }, [data])

    return (
        <>
            <ValidationBookedContainers
                selectedContainers={data}
                requiredContainers={requiredContainers}
                isInternalType={isInternalType}
                disabled={disabled}
            />
            <BookedContainersTable
                data={data}
                onChange={onChange}
                disabled={disabled}
                isInternalType={isInternalType}
            />
            <ButtonContainer>
                <Button
                    label="reject containers"
                    disabled={disabled || !selectedContainersToReject.length}
                    saving={isLoadingRejectContainers}
                    onClick={() => {
                        mutateRejectContainers({
                            orderNumber,
                            orderVersion,
                            selectedContainers: selectedContainersToReject,
                        })
                    }}
                    data-testid="btn-reject-containers"
                />
                <Button
                    label="rebook containers"
                    disabled={disabled || !selectedContainersToRebook.length}
                    saving={isLoadingRebookContainers}
                    onClick={() => {
                        mutateRebookContainers({
                            orderNumber,
                            orderVersion,
                            selectedContainers: selectedContainersToRebook,
                        })
                    }}
                    data-testid="btn-rebook-containers"
                />
            </ButtonContainer>
        </>
    )
}

export default BookedContainers
